import { plainToInstance, Type } from 'class-transformer'
import {
  RegisterScheduledRecalculationTime,
  RegisterTemplatePermittedActions,
  RegisterTemplateStatus,
  RegisterTemplateUniqueIdConfig,
} from './types'
import { IRegisterTemplate } from '@core/entities/register/RegisterTemplate/IRegisterTemplate'
import _find from 'lodash/find'
import { Entity } from '@core/entities/entity'
import { RegisterTemplateProperty } from '@core/entities/register/RegisterTemplateProperty/RegisterTemplateProperty'
import {
  RegisterItem,
  RegisterItemFlatPropertyData,
} from '@core/entities/register/RegisterItem'
import { RegisterItemColumn } from '@core/entities/register/RegisterItemColumn'
import { RegisterTemplateDiagram } from '@core/entities/register/RegisterTemplateDiagram'
import { Prop } from '@core/formula/types'
import { evaluate } from '@core/formula'
import { RegisterTemplateDiagramLabelType } from '@core/entities/SiteBoxDocumentPSPDFKitAnnotation'
import { statuses } from '@core/theme/statuses'

export class RegisterTemplate extends Entity<IRegisterTemplate> {
  id: number
  modifiedOn: string
  name: string
  description: string
  status: RegisterTemplateStatus
  statusBeforeArchiving: RegisterTemplateStatus
  permittedActions: RegisterTemplatePermittedActions[]
  registerProperties?: RegisterTemplateProperty[]

  @Type(() => RegisterItemColumn)
  flattenLinksRegisterProperties?: RegisterItemColumn[]

  @Type(() => RegisterTemplateDiagram)
  diagram?: RegisterTemplateDiagram

  scheduledRecalculationTime?: RegisterScheduledRecalculationTime
  uniqueIdSystemControlled?: boolean
  uniqueIdConfig: RegisterTemplateUniqueIdConfig
  version?: { id: number }

  public static new(payload: unknown): RegisterTemplate {
    const entity = plainToInstance(RegisterTemplate, payload)

    return entity
  }

  public isPublished(): boolean {
    return this.status === 'LIVE'
  }

  public isArchived(): boolean {
    return this.status === 'ARCHIVED'
  }

  public canEdit(): boolean {
    return this.permittedActions.includes('EDIT')
  }

  public canRecalculateFormulas(): boolean {
    return this.permittedActions.includes('RECALCULATE_FORMULAS')
  }

  public isDiagramEnabled(): boolean {
    return !!this.diagram?.enabled
  }

  public getStatusChipColor() {
    switch (this.status) {
      case 'DRAFT':
        return statuses.draft.color
      case 'ARCHIVED':
        return statuses.archived.color
      default:
        return statuses.active.color
    }
  }

  public static statusString(status: RegisterTemplateStatus): string {
    switch (status) {
      case 'DRAFT':
        return statuses.draft.label
      case 'ARCHIVED':
        return statuses.archived.label
      case 'LIVE':
        return statuses.active.label
      default:
        return status
    }
  }

  public getStatusString(): string {
    return RegisterTemplate.statusString(this.status)
  }

  public getDefaultFormValues = (
    initialValues: RegisterItem['dataWithLinkProperties'],
  ) => {
    return this.registerProperties.reduce((result, column) => {
      const { id } = column

      const diagramColorPropertyId = this.diagram?.colourProperty?.id
      const diagramColorUuid =
        this.diagram?.colourProperty?.selectConfig?.[0]?.uuid

      let defaultValue: string | number | object = {
        ...column,
        registerPropertyId: id,
        ...(diagramColorUuid && diagramColorPropertyId === String(id)
          ? { selectedOptionUuid: diagramColorUuid }
          : {}),
      }

      const registerItemColumn = _find(
        initialValues,
        (item) => item.registerPropertyId === id,
      )

      if (registerItemColumn && registerItemColumn.registerPropertyId === id) {
        switch (registerItemColumn.type) {
          case 'STRING':
            if (registerItemColumn?.formulaConfig) return result
            defaultValue = {
              ...registerItemColumn,
              textValue: registerItemColumn?.textValue,
            }

            break
          case 'SELECT':
            defaultValue = {
              ...registerItemColumn,
              selectedOptionUuid: registerItemColumn?.selectedOption?.uuid,
            }
            break
          case 'DATE':
            if (registerItemColumn?.formulaConfig) return result
            defaultValue = {
              ...registerItemColumn,
              dateValue: registerItemColumn.dateValue,
            }
            break
          case 'NUMBER':
            if (registerItemColumn?.formulaConfig) return result
            defaultValue = {
              ...registerItemColumn,
              numberValue: registerItemColumn.numberValue,
            }
            break
          case 'LINK':
            defaultValue = {
              ...registerItemColumn,
              parentRegisterItemId: registerItemColumn.parentRegisterItem?.id,
            }
            break
          case 'USER':
            defaultValue = {
              ...registerItemColumn,
              selectedUserId: registerItemColumn?.selectedUser?.id,
            }
            break
          case 'DIAGRAM':
            defaultValue = {
              ...registerItemColumn,
              annotationSetId: registerItemColumn?.annotationSetId ?? null,
              annotations: registerItemColumn?.annotations ?? [],
            }
            break
          default:
            defaultValue = registerItemColumn
            break
        }
      }

      return { ...result, [id]: defaultValue }
    }, {})
  }

  public getSubmitData = (formData) => {
    return this.registerProperties
      .filter((p) => !p.formulaConfig && !p.prefix)
      .filter((p) => p.type !== 'ATTACHMENT')
      .map((property) => {
        if (property.type === 'SELECT') {
          return {
            ...formData[property.id],
            selectedOptionUuid:
              formData[property.id].selectedOptionUuid === 'noSelection'
                ? null
                : formData[property.id].selectedOptionUuid,
          }
        } else if (property.type === 'NUMBER') {
          return {
            ...formData[property.id],
            numberValue:
              formData[property.id].numberValue === 'noSelection'
                ? null
                : formData[property.id].numberValue,
          }
        } else return formData[property.id]
      })
  }

  // Function to transform rows into the required data format
  private transformRowToLabelData = (
    rows: RegisterItemFlatPropertyData[],
    item: RegisterItem,
  ): Record<string, Prop & { id: string }> => {
    const data = {}
    rows.forEach((row) => {
      const { name, type, registerPropertyId } = row
      data[name] = {
        type,
        value: item.getRegisterItemPropertyValues(row),
        id: registerPropertyId,
        options:
          this?.flattenLinksRegisterProperties
            ?.find(({ id }) => id === registerPropertyId)
            ?.selectConfig?.map((option) => option.label) ?? [],
      }
    })
    return data
  }

  public getDiagramLabels(
    item: RegisterItem,
  ): RegisterTemplateDiagramLabelType[] {
    if (!item) return []

    const data = this.transformRowToLabelData(item.data, item)

    return (
      this.diagram?.labels?.reduce((acc, { property, formula }) => {
        const itemProperty = item.data.find(
          (d) => d.registerPropertyId === property.id,
        )

        const propertyValue = item.getRegisterItemPropertyValues(itemProperty)
        let propertyColor = ''

        if (formula) {
          const result = evaluate(data, formula.function ?? [], {
            type: 'BOOLEAN',
          })

          if (result.value) {
            // Only add to the array if resolvedValue is not empty
            propertyColor =
              property.type === 'SELECT'
                ? item.getRegisterItemColor(property.id)
                : ''
            acc.push({
              name: property.name,
              label: propertyValue,
              color: propertyColor,
              enabled: true,
            })
          }
        } else {
          if (propertyValue) {
            propertyColor =
              itemProperty?.type === 'SELECT'
                ? item.getRegisterItemColor(property.id)
                : ''

            // Only add to the array if resolvedValue is not empty
            acc.push({
              name: property.name,
              label: propertyValue,
              color: propertyColor,
              enabled: true,
            })
          }
        }
        return acc
      }, []) ?? []
    )
  }

  public getPropertiesList(
    excludeUniqueIdAndDescription?: boolean,
  ): RegisterItemColumn[] {
    return (
      this.flattenLinksRegisterProperties?.filter(
        (property) =>
          !excludeUniqueIdAndDescription ||
          (!property.isUniqueId() && !property.isDescription),
      ) ?? []
    )
  }

  public getUsedForPreviewPropertyIds(): string[] {
    return (
      this.getPropertiesList(true)
        .filter((property) => property.isUsedForPreview)
        .map((property) => property.id.toString()) ?? []
    )
  }
}
